import React, { useEffect } from 'react';
import OnYourAgenda from './sections/messages/OnYourAgenda';
import { DashboardData } from 'apis/DashboardAPI';
import Pulse, { ActivityType } from './sections/pulse/Pulse';
import { scrollToTop } from 'util/scroll';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { usePostableSources } from 'apis/CompanyAPI/users/usePostableSources';
import { sectionSpacing, contentSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import styled from '@emotion/styled';
import FeaturedCompanyMatchesForCurrentUser from 'pages/Discover/FeaturedCompanyMatchesForCurrentUser';
import { getOrUndefined } from 'util/resource';
import CompleteProfileMessage from './sections/messages/CompleteProfileMessage';
import DashboardPostInputField from './sections/DashboardPostInputField';

import Resources from 'util/resource/Resources';
import { SelfUserProfile } from 'types/user';
import RaisingCapitalMessage from './sections/messages/RaisingCapitalMessage';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';

const MAIN_COL_WIDTH = '800px';
const SIDEBAR_COL_WIDTH = '600px';

const GridContainer = styled.div<{ shouldShowPostField: boolean }>`
  display: grid;
  grid-template-columns: ${MAIN_COL_WIDTH} ${SIDEBAR_COL_WIDTH};
  grid-template-rows: ${props => (props.shouldShowPostField ? 'auto 1fr' : '1fr')};
  grid-template-areas: ${props =>
    props.shouldShowPostField ? "'post-field sidebar' 'feed sidebar'" : "'feed sidebar'"};
  column-gap: ${sectionSpacing};
  row-gap: ${contentSpacing};

  ${bluePlanetTheme.breakpoints.down(1700)} {
    grid-template-columns: ${MAIN_COL_WIDTH} 3fr;
  }

  ${bluePlanetTheme.breakpoints.down(1600)} {
    column-gap: 2rem;
  }

  ${bluePlanetTheme.breakpoints.down(1500)} {
    grid-template-columns: 4fr 3fr;
    column-gap: 1rem;
  }

  ${bluePlanetTheme.breakpoints.down('lg')} {
    grid-template-columns: 100%;
    grid-template-areas: ${props => (props.shouldShowPostField ? "'post-field' 'sidebar' 'feed'" : "'sidebar' 'feed'")};
    column-gap: 0;
    row-gap: ${contentSpacing};
  }
`;

const BannerWrapper = styled.div`
  margin-bottom: ${contentSpacing};
  max-width: calc(${MAIN_COL_WIDTH} + ${sectionSpacing} + ${SIDEBAR_COL_WIDTH});

  ${bluePlanetTheme.breakpoints.down('lg')} {
    max-width: 100%;
  }
`;

function getSelectableActivityTypes(user: SelfUserProfile) {
  return [
    {
      type: 'For you',
      condition: user.canBrowsePlatform,
    },
    {
      type: 'From your network',
      condition: true,
    },
    {
      type: 'From your collections',
      condition: true,
    },
  ]
    .filter(it => it.condition)
    .map(it => it.type) as ActivityType[];
}

export function Home({ dashboardData }: { dashboardData: DashboardData }): React.ReactNode {
  const messages = dashboardData.messages;
  const { isMobile } = useBreakpoints();
  const { resource: userProfileResource } = useSelfUserProfile();
  const { resource: postableSourcesResource } = usePostableSources();
  const currentYear = new Date().getFullYear();
  const raisingCapitalMessage = useCloseableMessage(`raising-capital-${currentYear}`);
  const [newlyPostedUpdateId, setNewlyPostedUpdateId] = React.useState<string | undefined>();

  useEffect(() => {
    scrollToTop();
  }, []);

  // Reset the newlyPostedUpdateId after 3 seconds
  useEffect(() => {
    if (newlyPostedUpdateId) {
      const timer = setTimeout(() => {
        setNewlyPostedUpdateId(undefined);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [newlyPostedUpdateId]);

  const handleNewUpdatePosted = React.useCallback((updateId: string) => {
    setNewlyPostedUpdateId(updateId);
  }, []);

  const userProfile = getOrUndefined(userProfileResource);
  const postableSources = getOrUndefined(postableSourcesResource);
  const shouldShowPostField = !!(
    userProfile &&
    postableSources &&
    (postableSources.companies.length > 0 ||
      postableSources.communities.length > 0 ||
      postableSources.self.feeds.length > 0)
  );

  const shouldShowBanner =
    messages.find(message => message.type === 'raising-capital-2025') && !raisingCapitalMessage.isClosed;

  return (
    <>
      {shouldShowBanner && (
        <BannerWrapper>
          <RaisingCapitalMessage isMobile={isMobile} onClose={raisingCapitalMessage.closeMessage} />
        </BannerWrapper>
      )}
      <GridContainer shouldShowPostField={shouldShowPostField}>
        {shouldShowPostField && (
          <div style={{ gridArea: 'post-field' }}>
            <DashboardPostInputField
              userProfile={userProfile!}
              postableSources={postableSources!}
              onNewUpdatePosted={handleNewUpdatePosted}
            />
          </div>
        )}
        <div style={{ gridArea: 'sidebar', display: 'flex', flexDirection: 'column', gap: contentSpacing }}>
          {messages.length > 0 && (
            <>
              <OnYourAgenda messages={messages} />
              <CompleteProfileMessage messages={messages} />
            </>
          )}
          {getOrUndefined(userProfileResource)?.canBrowsePlatform && <FeaturedCompanyMatchesForCurrentUser />}
        </div>
        <div style={{ gridArea: 'feed' }}>
          <Resources resources={[userProfileResource]}>
            {([user]) => {
              const selectableActivityTypes = getSelectableActivityTypes(user);

              const initialActivityType = selectableActivityTypes[0];

              return (
                <Pulse
                  activityTypes={selectableActivityTypes}
                  initialActivityType={initialActivityType}
                  newlyPostedUpdateId={newlyPostedUpdateId}
                  enableScrollToUpdate
                />
              );
            }}
          </Resources>
        </div>
      </GridContainer>
    </>
  );
}
